/**
 * Solidity Language Definition for Monaco Editor
 * Provides syntax highlighting, completions, hover info, and error markers.
 */
import type { CancellationToken, editor, IRange, languages, Position } from 'monaco-editor';

/* ================================================================== */
/*  Solidity Language Tokens                                          */
/* ================================================================== */

export const solidityLanguageDef: languages.IMonarchLanguage = {
  defaultToken: '',
  tokenPostfix: '.sol',

  keywords: [
    'pragma',
    'solidity',
    'import',
    'from',
    'as',
    'is',
    'contract',
    'interface',
    'library',
    'abstract',
    'function',
    'modifier',
    'event',
    'error',
    'struct',
    'enum',
    'constructor',
    'receive',
    'fallback',
    'mapping',
    'returns',
    'return',
    'if',
    'else',
    'for',
    'while',
    'do',
    'break',
    'continue',
    'try',
    'catch',
    'revert',
    'require',
    'assert',
    'emit',
    'new',
    'delete',
    'type',
    'using',
    'assembly',
    'unchecked',
  ],

  typeKeywords: [
    'address',
    'bool',
    'string',
    'bytes',
    'uint',
    'int',
    'uint8',
    'uint16',
    'uint32',
    'uint64',
    'uint128',
    'uint256',
    'int8',
    'int16',
    'int32',
    'int64',
    'int128',
    'int256',
    'bytes1',
    'bytes2',
    'bytes3',
    'bytes4',
    'bytes8',
    'bytes16',
    'bytes20',
    'bytes32',
    'fixed',
    'ufixed',
  ],

  modifiers: [
    'public',
    'private',
    'internal',
    'external',
    'pure',
    'view',
    'payable',
    'nonpayable',
    'virtual',
    'override',
    'immutable',
    'constant',
    'indexed',
    'anonymous',
    'memory',
    'storage',
    'calldata',
  ],

  builtins: [
    'msg',
    'block',
    'tx',
    'abi',
    'this',
    'super',
    'selfdestruct',
    'suicide',
    'keccak256',
    'sha256',
    'ripemd160',
    'ecrecover',
    'addmod',
    'mulmod',
    'blockhash',
    'gasleft',
  ],

  operators: [
    '=',
    '>',
    '<',
    '!',
    '~',
    '?',
    ':',
    '==',
    '<=',
    '>=',
    '!=',
    '&&',
    '||',
    '++',
    '--',
    '+',
    '-',
    '*',
    '/',
    '&',
    '|',
    '^',
    '%',
    '<<',
    '>>',
    '>>>',
    '>>>',
    '+=',
    '-=',
    '*=',
    '/=',
    '&=',
    '|=',
    '^=',
    '%=',
    '<<=',
    '>>=',
    '=>',
  ],

  symbols: /[=><!~?:&|+\-*/^%]+/,
  escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
  digits: /\d+(_+\d+)*/,
  octaldigits: /[0-7]+(_+[0-7]+)*/,
  binarydigits: /[0-1]+(_+[0-1]+)*/,
  hexdigits: /[[0-9a-fA-F]+(_+[0-9a-fA-F]+)*/,

  tokenizer: {
    root: [
      // NatSpec / doc comments
      [/\/\/\/.*$/, 'comment.doc'],
      [/\/\*\*/, 'comment.doc', '@docComment'],
      // Regular comments
      [/\/\/.*$/, 'comment'],
      [/\/\*/, 'comment', '@comment'],

      // Pragma
      [/pragma/, 'keyword', '@pragma'],

      // Imports
      [/import/, 'keyword'],

      // Numbers
      [/0[xX]@hexdigits/, 'number.hex'],
      [/0[oO]?@octaldigits/, 'number.octal'],
      [/0[bB]@binarydigits/, 'number.binary'],
      [/@digits[eE]([-+]?@digits)?/, 'number.float'],
      [/@digits\.@digits([eE][-+]?@digits)?/, 'number.float'],
      [/@digits/, 'number'],

      // Strings
      [/"([^"\\]|\\.)*$/, 'string.invalid'],
      [/"/, 'string', '@string_double'],
      [/'([^'\\]|\\.)*$/, 'string.invalid'],
      [/'/, 'string', '@string_single'],

      // Identifiers
      [
        /[a-zA-Z_$][\w$]*/,
        {
          cases: {
            '@keywords': 'keyword',
            '@typeKeywords': 'type',
            '@modifiers': 'keyword.modifier',
            '@builtins': 'variable.predefined',
            '@default': 'identifier',
          },
        },
      ],

      // Whitespace
      { include: '@whitespace' },

      // Delimiters & operators
      [/[{}()[\]]/, '@brackets'],
      [/[<>](?!@symbols)/, '@brackets'],
      [/@symbols/, { cases: { '@operators': 'operator', '@default': '' } }],

      // Separator
      [/[;,.]/, 'delimiter'],
    ],

    comment: [
      [/[^/*]+/, 'comment'],
      [/\*\//, 'comment', '@pop'],
      [/[/*]/, 'comment'],
    ],

    docComment: [
      [/[^/*]+/, 'comment.doc'],
      [/\*\//, 'comment.doc', '@pop'],
      [/[/*]/, 'comment.doc'],
    ],

    pragma: [
      [/;/, 'delimiter', '@pop'],
      [/[^;]+/, 'keyword.pragma'],
    ],

    string_double: [
      [/[^\\"]+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/"/, 'string', '@pop'],
    ],

    string_single: [
      [/[^\\']+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/'/, 'string', '@pop'],
    ],

    whitespace: [[/[ \t\r\n]+/, '']],
  },
};

/* ================================================================== */
/*  Language Configuration                                            */
/* ================================================================== */

export const solidityLanguageConfig: languages.LanguageConfiguration = {
  comments: {
    lineComment: '//',
    blockComment: ['/*', '*/'],
  },
  brackets: [
    ['{', '}'],
    ['[', ']'],
    ['(', ')'],
  ],
  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"', notIn: ['string'] },
    { open: "'", close: "'", notIn: ['string'] },
  ],
  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
  ],
  folding: {
    markers: {
      start: /^\s*\/\/ #?region\b/,
      end: /^\s*\/\/ #?endregion\b/,
    },
  },
  indentationRules: {
    increaseIndentPattern: /^.*\{[^}"']*$/,
    decreaseIndentPattern: /^(.*\*\/)?\s*\}/,
  },
};

/* ================================================================== */
/*  Completion Snippets                                               */
/* ================================================================== */

const SOLIDITY_SNIPPETS: Array<{
  label: string;
  kind: 'Snippet' | 'Keyword' | 'Function';
  insertText: string;
  detail: string;
  documentation?: string;
}> = [
  {
    label: 'contract',
    kind: 'Snippet',
    insertText: [
      '// SPDX-License-Identifier: MIT',
      'pragma solidity ^0.8.20;',
      '',
      'contract ${1:ContractName} {',
      '\t$0',
      '}',
    ].join('\n'),
    detail: 'Create a new contract',
    documentation: 'Scaffold a new Solidity contract with license and pragma.',
  },
  {
    label: 'function',
    kind: 'Snippet',
    insertText: [
      'function ${1:functionName}(${2:params}) ${3|public,external,internal,private|} ${4|view,pure,payable,|} returns (${5:returnType}) {',
      '\t$0',
      '}',
    ].join('\n'),
    detail: 'Function with visibility and mutability',
  },
  {
    label: 'event',
    kind: 'Snippet',
    insertText: 'event ${1:EventName}(${2:params});',
    detail: 'Declare an event',
  },
  {
    label: 'modifier',
    kind: 'Snippet',
    insertText: [
      'modifier ${1:modifierName}(${2:params}) {',
      '\t${3:require(condition, "Error");}',
      '\t_;',
      '}',
    ].join('\n'),
    detail: 'Declare a modifier',
  },
  {
    label: 'mapping',
    kind: 'Snippet',
    insertText:
      'mapping(${1:address} => ${2:uint256}) ${3|public,private,internal|} ${4:variableName};',
    detail: 'Declare a mapping',
  },
  {
    label: 'struct',
    kind: 'Snippet',
    insertText: ['struct ${1:StructName} {', '\t${2:uint256 field1};', '\t$0', '}'].join('\n'),
    detail: 'Declare a struct',
  },
  {
    label: 'enum',
    kind: 'Snippet',
    insertText: ['enum ${1:EnumName} {', '\t${2:Value1},', '\t${3:Value2}', '}'].join('\n'),
    detail: 'Declare an enum',
  },
  {
    label: 'constructor',
    kind: 'Snippet',
    insertText: ['constructor(${1:params}) {', '\t$0', '}'].join('\n'),
    detail: 'Declare a constructor',
  },
  {
    label: 'require',
    kind: 'Function',
    insertText: 'require(${1:condition}, "${2:Error message}");',
    detail: 'Require statement with error message',
  },
  {
    label: 'emit',
    kind: 'Keyword',
    insertText: 'emit ${1:EventName}(${2:args});',
    detail: 'Emit an event',
  },
  {
    label: 'erc20-import',
    kind: 'Snippet',
    insertText: 'import "@openzeppelin/contracts/token/ERC20/ERC20.sol";',
    detail: 'Import OpenZeppelin ERC-20',
  },
  {
    label: 'erc721-import',
    kind: 'Snippet',
    insertText: 'import "@openzeppelin/contracts/token/ERC721/ERC721.sol";',
    detail: 'Import OpenZeppelin ERC-721',
  },
  {
    label: 'ownable-import',
    kind: 'Snippet',
    insertText: 'import "@openzeppelin/contracts/access/Ownable.sol";',
    detail: 'Import OpenZeppelin Ownable',
  },
  {
    label: 'receive',
    kind: 'Snippet',
    insertText: ['receive() external payable {', '\t$0', '}'].join('\n'),
    detail: 'Receive Ether function',
  },
  {
    label: 'fallback',
    kind: 'Snippet',
    insertText: ['fallback() external payable {', '\t$0', '}'].join('\n'),
    detail: 'Fallback function',
  },
  {
    label: 'error',
    kind: 'Snippet',
    insertText: 'error ${1:ErrorName}(${2:params});',
    detail: 'Custom error declaration (gas-efficient)',
  },
  {
    label: 'interface',
    kind: 'Snippet',
    insertText: [
      'interface ${1:InterfaceName} {',
      '\tfunction ${2:functionName}(${3:params}) external ${4|view,pure,|} returns (${5:returnType});',
      '}',
    ].join('\n'),
    detail: 'Declare an interface',
  },
  {
    label: 'library',
    kind: 'Snippet',
    insertText: ['library ${1:LibraryName} {', '\t$0', '}'].join('\n'),
    detail: 'Declare a library',
  },
  {
    label: 'try-catch',
    kind: 'Snippet',
    insertText: [
      'try ${1:externalCall}(${2:args}) returns (${3:returnType} ${4:result}) {',
      '\t$0',
      '} catch Error(string memory reason) {',
      '\trevert(reason);',
      '} catch (bytes memory) {',
      '\trevert("Unknown error");',
      '}',
    ].join('\n'),
    detail: 'Try-catch for external calls',
  },
];

/* ================================================================== */
/*  Hover Provider Data                                               */
/* ================================================================== */

const HOVER_DATA: Record<string, { detail: string; documentation: string }> = {
  msg: {
    detail: 'msg (Message)',
    documentation:
      'Global variable containing call data.\n\n' +
      '- `msg.sender` — address of caller\n' +
      '- `msg.value` — ETH sent with call (wei)\n' +
      '- `msg.data` — complete calldata\n' +
      '- `msg.sig` — first 4 bytes of calldata (function selector)',
  },
  block: {
    detail: 'block (Block)',
    documentation:
      'Global variable with block information.\n\n' +
      '- `block.timestamp` — current block timestamp (seconds since epoch)\n' +
      '- `block.number` — current block number\n' +
      '- `block.chainid` — chain ID\n' +
      '- `block.prevrandao` — previous RANDAO value',
  },
  tx: {
    detail: 'tx (Transaction)',
    documentation:
      'Global variable with transaction info.\n\n- `tx.origin` — sender of the transaction\n- `tx.gasprice` — gas price of the transaction',
  },
  require: {
    detail: 'require(condition, message?)',
    documentation:
      'Reverts the transaction if condition is false.\n\nUsage: `require(balance >= amount, "Insufficient balance");`\n\nPrefer custom errors (`revert InsufficientBalance()`) for gas efficiency in Solidity ≥0.8.4.',
  },
  keccak256: {
    detail: 'keccak256(bytes memory)',
    documentation:
      'Computes the Keccak-256 hash of the given bytes.\n\n`bytes32 hash = keccak256(abi.encodePacked(value));`',
  },
  payable: {
    detail: 'payable',
    documentation:
      'Allows a function to receive Ether. Functions not marked payable will reject any Ether sent to them.',
  },
  view: {
    detail: 'view',
    documentation: 'Promise not to modify state. Can read state variables but cannot write.',
  },
  pure: {
    detail: 'pure',
    documentation: 'Promise not to read or modify state. Cannot access state variables.',
  },
  memory: {
    detail: 'memory',
    documentation:
      'Data location: temporary, exists only during function execution. Default for function parameters of reference types.',
  },
  storage: {
    detail: 'storage',
    documentation:
      'Data location: persistent on-chain storage. State variables are always in storage.',
  },
  calldata: {
    detail: 'calldata',
    documentation:
      'Data location: read-only, non-modifiable area for function arguments in external functions. More gas-efficient than memory for external function parameters.',
  },
  abi: {
    detail: 'abi (ABI Encoding)',
    documentation:
      'Built-in functions for ABI encoding/decoding.\n\n' +
      '- `abi.encode(...)` — ABI-encode given arguments\n' +
      '- `abi.encodePacked(...)` — non-standard packed encoding\n' +
      '- `abi.encodeWithSelector(bytes4, ...)` — encode with function selector\n' +
      '- `abi.decode(bytes, (types))` — ABI-decode data',
  },
};

/* ================================================================== */
/*  Registration                                                      */
/* ================================================================== */

export function registerSolidityLanguage(monaco: typeof import('monaco-editor')): void {
  // Register language if not already registered
  const languages = monaco.languages.getLanguages();
  if (languages.some((l) => l.id === 'sol')) return;

  monaco.languages.register({
    id: 'sol',
    extensions: ['.sol'],
    aliases: ['Solidity', 'solidity', 'sol'],
    mimetypes: ['text/x-solidity'],
  });

  // Set language tokens
  monaco.languages.setMonarchTokensProvider('sol', solidityLanguageDef);

  // Set language configuration
  monaco.languages.setLanguageConfiguration('sol', solidityLanguageConfig);

  // Completion provider
  monaco.languages.registerCompletionItemProvider('sol', {
    provideCompletionItems: (
      model: editor.ITextModel,
      position: Position,
      _context: languages.CompletionContext,
      _token: CancellationToken,
    ): languages.ProviderResult<languages.CompletionList> => {
      const word = model.getWordUntilPosition(position);
      const range: IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const kindMap: Record<string, languages.CompletionItemKind> = {
        Snippet: monaco.languages.CompletionItemKind.Snippet,
        Keyword: monaco.languages.CompletionItemKind.Keyword,
        Function: monaco.languages.CompletionItemKind.Function,
      };

      const suggestions: languages.CompletionItem[] = SOLIDITY_SNIPPETS.map((s) => ({
        label: s.label,
        kind: kindMap[s.kind] ?? monaco.languages.CompletionItemKind.Snippet,
        insertText: s.insertText,
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        detail: s.detail,
        documentation: s.documentation ?? s.detail,
        range,
      }));

      return { suggestions };
    },
  });

  // Hover provider
  monaco.languages.registerHoverProvider('sol', {
    provideHover: (
      model: editor.ITextModel,
      position: Position,
      _token: CancellationToken,
    ): languages.ProviderResult<languages.Hover> => {
      const word = model.getWordAtPosition(position);
      if (!word) return null;

      const info = HOVER_DATA[word.word];
      if (!info) return null;

      return {
        range: {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        },
        contents: [{ value: `**${info.detail}**` }, { value: info.documentation }],
      };
    },
  });

  // Custom theme enhancements for Solidity
  monaco.editor.defineTheme('kubernodes-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword.sol', foreground: '569cd6', fontStyle: 'bold' },
      { token: 'keyword.modifier.sol', foreground: 'c586c0' },
      { token: 'keyword.pragma.sol', foreground: '4ec9b0' },
      { token: 'type.sol', foreground: '4ec9b0' },
      { token: 'variable.predefined.sol', foreground: 'dcdcaa' },
      { token: 'comment.doc.sol', foreground: '608b4e', fontStyle: 'italic' },
      { token: 'number.hex.sol', foreground: 'b5cea8' },
    ],
    colors: {
      'editor.background': '#1e1e1e',
      'editor.foreground': '#d4d4d4',
      'editorCursor.foreground': '#ffffff',
      'editor.selectionBackground': '#264f78',
      'editor.lineHighlightBackground': '#2a2d2e',
      'editorLineNumber.foreground': '#858585',
      'editorLineNumber.activeForeground': '#c6c6c6',
    },
  });
}
